<template>
    <div class="modal-card card">
        <form @submit.prevent="onSubmit">
            <div class="card-header">
                <!-- Title -->
                <h4 class="card-header-title">Upload signed certificate</h4>
                <!-- Close -->
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>

            <div class="card-body">
                <dl class="pairs">
                    <div>
                        <dt>Certificate</dt>
                        <dd>{{ modalData.commonName }}</dd>
                    </div>
                </dl>

                <p class="step-say">Paste the certificate your certificate authority signed, or drop the file it sent
                    you. Deployment to the edge starts as soon as it is accepted.</p>

                <!-- Key algorithm -->
                <div class="field">
                    <div class="field-label">Key algorithm <span class="req">*</span></div>
                    <div class="d-flex gap-3 flex-wrap">
                        <div class="form-check mt-0" v-for="option in keyAlgOptions" :key="option.value">
                            <input class="form-check-input" type="radio" name="keyAlgorithm"
                                :id="`keyAlg-${option.value}`" :value="option.value"
                                v-model="signedCertificate.keyAlgorithm">
                            <label class="form-check-label" :for="`keyAlg-${option.value}`">{{ option.label }}</label>
                        </div>
                    </div>
                    <p class="field-hint mb-0">
                        Match the request you had signed — the downloaded CSR file names carry the algorithm.
                    </p>
                </div>

                <!-- Signed certificate -->
                <div class="field">
                    <div class="field-label">Signed certificate <span class="req">*</span></div>
                    <div class="pem-field" :class="{ 'is-dragging': dragging === 'certificate' }"
                        @dragover.prevent="dragging = 'certificate'" @dragleave.prevent="dragging = null"
                        @drop.prevent="onDrop('certificate', $event)">
                        <textarea class="pem-input" rows="6" spellcheck="false"
                            placeholder="-----BEGIN CERTIFICATE-----&#10;…&#10;-----END CERTIFICATE-----"
                            v-model="signedCertificate.certificate"></textarea>
                        <div class="pem-foot">
                            <button type="button" class="btn btn-sm btn-light" @click="pickFile('certificate')">
                                Choose file
                            </button>
                            <span class="pem-meta" v-if="fileNames.certificate">{{ fileNames.certificate }}</span>
                            <span class="pem-meta" v-else>or drop a .pem file here</span>
                            <button type="button" class="btn btn-sm btn-light ms-auto"
                                v-if="signedCertificate.certificate" @click="clearField('certificate')">Clear</button>
                        </div>
                    </div>
                    <p class="field-hint mb-0" v-if="!signedCertificate.certificate">
                        The certificate your CA returned, in PEM format.
                    </p>
                    <p class="field-check mb-0" v-else-if="certificateBlocks">
                        Reads as PEM · {{ certificateBlocks }} certificate{{ certificateBlocks === 1 ? '' : 's' }}
                        <span v-if="certificateBlocks > 1">— the extras belong in the trust chain below</span>
                    </p>
                    <p class="field-error mb-0" v-else>
                        This does not contain a <span class="mono">-----BEGIN CERTIFICATE-----</span> block.
                    </p>
                </div>

                <!-- Trust chain -->
                <div class="field">
                    <div class="field-label">Trust chain <span class="req">*</span></div>
                    <div class="pem-field" :class="{ 'is-dragging': dragging === 'trustChain' }"
                        @dragover.prevent="dragging = 'trustChain'" @dragleave.prevent="dragging = null"
                        @drop.prevent="onDrop('trustChain', $event)">
                        <textarea class="pem-input" rows="4" spellcheck="false"
                            placeholder="-----BEGIN CERTIFICATE-----&#10;…"
                            v-model="signedCertificate.trustChain"></textarea>
                        <div class="pem-foot">
                            <button type="button" class="btn btn-sm btn-light" @click="pickFile('trustChain')">
                                Choose file
                            </button>
                            <span class="pem-meta" v-if="fileNames.trustChain">{{ fileNames.trustChain }}</span>
                            <span class="pem-meta" v-else>or drop a .pem file here</span>
                            <button type="button" class="btn btn-sm btn-light ms-auto"
                                v-if="signedCertificate.trustChain" @click="clearField('trustChain')">Clear</button>
                        </div>
                    </div>
                    <p class="field-hint mb-0" v-if="!signedCertificate.trustChain">
                        The intermediate certificates your CA provided, in PEM format.
                    </p>
                    <p class="field-check mb-0" v-else-if="trustChainBlocks">
                        Reads as PEM · {{ trustChainBlocks }} certificate{{ trustChainBlocks === 1 ? '' : 's' }}
                    </p>
                    <p class="field-error mb-0" v-else>
                        This does not contain a <span class="mono">-----BEGIN CERTIFICATE-----</span> block.
                    </p>
                </div>

                <input ref="fileInput" type="file" class="d-none" accept=".pem,.crt,.cer,.cert,.txt"
                    @change="onFileChosen">

                <div class="next-panel">
                    <div class="next-title">What happens next</div>
                    <p class="field-hint mb-0">aqfer verifies the certificate against the signing request, then deploys
                        it across the edge network. That usually takes 30–45 minutes, and the certificate now serving
                        traffic stays in place until it completes.</p>
                </div>

                <div class="alert alert-danger mb-0 mt-3" role="alert" v-if="error">{{ error }}</div>
            </div>

            <div class="card-footer d-flex align-items-center gap-3">
                <div class="ms-auto d-flex gap-2">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"
                        :disabled="creating">Cancel</button>
                    <button type="submit" class="btn btn-primary" :disabled="creating">
                        <div v-if="creating" class="spinner-border spinner-border-sm" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <div v-else>Upload certificate</div>
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import { mapActions } from "vuex";

const PEM_BLOCK = /-----BEGIN CERTIFICATE-----/g;

export default {
    name: "UpdateSignedCertificate",
    data() {
        return {
            creating: false,
            error: "",
            dragging: null,
            fileNames: { certificate: "", trustChain: "" },
            pickingFor: null,
            signedCertificate: {},
            keyAlgOptions: [
                { label: "ECDSA", value: "ECDSA" },
                { label: "RSA", value: "RSA" },
            ],
        };
    },
    props: {
        modalData: {
            type: [Object, Array, String],
        },
    },
    computed: {
        certificateBlocks() {
            return this.countBlocks(this.signedCertificate.certificate);
        },
        trustChainBlocks() {
            return this.countBlocks(this.signedCertificate.trustChain);
        },
    },
    methods: {
        ...mapActions("aut", {
            createSignedCertificate: "createSignedCertificate",
        }),
        countBlocks(value) {
            if (!value) return 0;
            return (String(value).match(PEM_BLOCK) || []).length;
        },
        pickFile(field) {
            this.pickingFor = field;
            this.$refs.fileInput.value = "";
            this.$refs.fileInput.click();
        },
        onFileChosen(event) {
            const file = event.target.files && event.target.files[0];
            if (file) this.readFile(this.pickingFor, file);
        },
        onDrop(field, event) {
            this.dragging = null;
            const file = event.dataTransfer?.files && event.dataTransfer.files[0];
            if (file) this.readFile(field, file);
        },
        readFile(field, file) {
            const reader = new FileReader();
            reader.onload = (res) => {
                this.signedCertificate[field] = String(res.target.result).replace(/[\n\r]+/g, "\n").trim();
                this.fileNames[field] = `${file.name} · ${this.formatSize(file.size)}`;
            };
            reader.onerror = () => {
                this.error = `Could not read ${file.name}. Paste the certificate instead.`;
            };
            reader.readAsText(file);
        },
        clearField(field) {
            delete this.signedCertificate[field];
            this.fileNames[field] = "";
        },
        formatSize(bytes) {
            if (bytes < 1024) return `${bytes} B`;
            return `${(bytes / 1024).toFixed(1)} KB`;
        },
        validate() {
            const { keyAlgorithm, certificate, trustChain } = this.signedCertificate;
            if (!keyAlgorithm) return "Select the key algorithm.";
            if (!certificate) return "Signed certificate is required.";
            if (!this.certificateBlocks) return "Signed certificate is not a valid PEM certificate.";
            if (!trustChain) return "Trust chain is required.";
            if (!this.trustChainBlocks) return "Trust chain is not a valid PEM certificate.";
            return "";
        },
        async onSubmit() {
            this.error = this.validate();
            if (this.error) return;
            this.creating = true;
            let toastOptions;
            await this.createSignedCertificate({
                commonName: this.modalData.commonName,
                payload: this.signedCertificate
            }).then(() => {
                toastOptions = {
                    message: "Signed certificate uploaded. Deployment has started.",
                    type: "success"
                }
                // Call success callback if provided
                if (this.modalData.onSuccess && typeof this.modalData.onSuccess === 'function') {
                    this.modalData.onSuccess();
                }
                this.$store.dispatch('hideModal')
            }).catch(err => {
                console.log("error: ", err)
                let error = null
                if (err.response)
                    error = err.response.data.error;
                else error = err.message;
                this.error = error || "Something went wrong while creating signed certificate. Please contact support.";
                toastOptions = {
                    message: this.error,
                    type: "error"
                }
            })
            this.creating = false;
            this.$store.dispatch("addToast", toastOptions);
        },
    },
};
</script>

<style lang="scss" scoped>
@use '../../theme/scss/user-variables' as *;

.pairs {
    margin: 0 0 1rem;
}

.pairs>div {
    display: grid;
    grid-template-columns: 6rem 1fr;
    gap: 1rem;
    align-items: baseline;
}

.pairs dt {
    font-weight: 400;
    color: #6E84A3;
    font-size: .8125rem;
    margin: 0;

    body.dark-mode & {
        color: $dark-text-secondary;
    }
}

.pairs dd {
    margin: 0;
    font-size: .8125rem;
    word-break: break-all;
}

.step-say {
    font-size: .8125rem;
    color: #6E84A3;
    margin: 0 0 1.25rem;

    body.dark-mode & {
        color: $dark-text-secondary;
    }
}

.field {
    margin-bottom: 1.25rem;
}

.field-label {
    font-size: .8125rem;
    font-weight: 500;
    margin-bottom: .4rem;
}

.req {
    color: #E63757;
}

.field-hint {
    font-size: .75rem;
    color: #95AAC9;
    margin-top: .35rem;

    body.dark-mode & {
        color: $dark-text-secondary;
    }
}

.field-check {
    font-size: .75rem;
    color: #0B7A4B;
    margin-top: .35rem;

    body.dark-mode & {
        color: #4ade8a;
    }
}

.field-error {
    font-size: .75rem;
    color: #E63757;
    margin-top: .35rem;

    body.dark-mode & {
        color: #f28a9c;
    }
}

.form-check-label {
    font-size: .8125rem;
}

/* PEM drop target ------------------------------------------------------- */
.pem-field {
    border: 1px solid #D2DDEC;
    border-radius: .375rem;
    overflow: hidden;
    transition: border-color .15s, box-shadow .15s;

    &:focus-within {
        border-color: #2C7BE5;
        box-shadow: 0 0 0 .18rem rgba(44, 123, 229, .12);
    }

    &.is-dragging {
        border-color: #2C7BE5;
        border-style: dashed;
        background: #F0F6FE;
    }

    body.dark-mode & {
        border-color: $dark-border-color;

        &.is-dragging {
            border-color: #4d90ea;
            background: rgba(44, 123, 229, .12);
        }
    }
}

.pem-input {
    display: block;
    width: 100%;
    border: none;
    resize: vertical;
    padding: .6rem .75rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: .75rem;
    line-height: 1.5;
    color: #12263F;
    background: transparent;

    &:focus {
        outline: none;
    }

    &::placeholder {
        color: #B1C2D9;
    }

    body.dark-mode & {
        color: $dark-text-primary;

        &::placeholder {
            color: $dark-text-muted;
        }
    }
}

.pem-foot {
    display: flex;
    align-items: center;
    gap: .6rem;
    flex-wrap: wrap;
    padding: .5rem .6rem;
    border-top: 1px solid #EDF2F9;
    background: #F9FBFD;

    body.dark-mode & {
        border-top-color: $dark-border-color;
        background: $dark-input-bg;
    }
}

.pem-meta {
    font-size: .75rem;
    color: #6E84A3;
    word-break: break-all;

    body.dark-mode & {
        color: $dark-text-secondary;
    }
}

.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

/* What happens next ------------------------------------------------------ */
.next-panel {
    margin-top: 1.5rem;
    background: #F0F6FE;
    border: 1px solid #BBD4F7;
    border-radius: .5rem;
    padding: .9rem 1.15rem;

    body.dark-mode & {
        background: rgba(44, 123, 229, .12);
        border-color: rgba(44, 123, 229, .35);
    }
}

.next-title {
    font-weight: 600;
    font-size: .875rem;
}
</style>
